/**
 * datasetOverview.ts — the advisor's pre-warmed terrain map (Advisor v2, spec §3).
 *
 * METADATA ONLY. This module builds from metadata RPCs exclusively — it must
 * never import a walk endpoint (enforced by the import-boundary test). Measure
 * values therefore cannot enter the overview by construction; figures reach the
 * model only through query_kpi tool results (anti-hallucination contract).
 *
 * Built once per drawer-open (useDatasetOverview) and consumed by:
 *   - assembleSystem (cached system prefix block)
 *   - WelcomeBlock (data-aware welcome template)
 *   - buildTools ctx (overview-informed tool errors, named-frame steering)
 *
 * All-or-nothing: any RPC failure rejects the whole build — a half-built
 * overview would be a silent wrong terrain map. The caller degrades loudly
 * (spec §3.6).
 */

import type { ApiClient } from '../../api/apiClient'
import { getKpisForPage } from '../../api/endpoints/getKpisForPage'
import { getCharts } from '../../api/endpoints/getCharts'
import { getControls } from '../../api/endpoints/getControls'
import type { Controls, CampaignTimeslotItem } from '../../api/endpoints/getControls'
import {
  toSurfacedRollingTimeframes,
  toAbsoluteTimeframes,
} from '../../components/Filters/TimeframeSelect'
import { predicateOf } from '../../state/campaignPredicate'
import { PAGE_CATALOG } from './pageCatalog'
import type { PageInfo } from './pageCatalog'

export interface OverviewDim {
  count: number
  samples: string[]
}

export interface OverviewPage {
  pageId: string
  route: string
  kpis: string[]
  fact: string
  capabilities: string[]
}

export interface DatasetOverview {
  pages: OverviewPage[]
  /** fact table → dim name → count + sample values (full lists live behind describe_page). */
  dims: Record<string, Record<string, OverviewDim>>
  /** kpi_root → value types with display labels (only roots that carry any). */
  valueTypes: Record<string, Array<{ key: string; label: string }>>
  timeframe: {
    modelFloor: string | null
    minDate: string | null
    maxDate: string | null
    namedFrames: Array<{ id: string; label: string; from: string; to: string }>
    /** filters: compact predicate summary ("Topic Level 3=fresh_start"), omitted when the
     *  campaign is window-only. item: the raw controls row — carried through so the
     *  set_campaign steer tool can build the exact selection write (buildCampaignSelectionParams),
     *  not re-derive it from the rendered summary. */
    campaigns: Array<{
      id: string
      label: string
      from: string
      to: string
      filters?: string
      item: CampaignTimeslotItem
    }>
  }
}

/**
 * Per-page control-surface gates as the page components hard-code them.
 * Mirrors ReviewPageScaffold wiring: RK/MI/Contribution pass showComparison,
 * Dynamics exposes comparison only in sub-period mode, CP/MDD hard-set it
 * false; the multi-timeframe overlay rides CP/MDD/Dynamics. VERIFY against the
 * page components when touching this map — it is pinned, not derived.
 */
export const PAGE_CAPABILITIES: Record<string, string[]> = {
  ReviewKpis: ['comparison surface'],
  MarketingInvests: ['comparison surface'],
  CampaignPerformance: ['multi-timeframe overlay'],
  Contribution: ['comparison surface', 'views: channels|groups'],
  Dynamics: ['comparison (sub-period mode)', 'multi-timeframe overlay'],
  MultiDeepDive: ['multi-timeframe overlay'],
}

const SAMPLE_LIMIT = 5

export async function buildDatasetOverview(apiClient: ApiClient): Promise<DatasetOverview> {
  const catalog = PAGE_CATALOG as Record<string, PageInfo>
  const pageIds = Object.keys(catalog)

  // Per page: KPI list + registry-resolved controls fact (describe_page pattern —
  // registry default_params.fact_table wins, catalog static fact is the fallback).
  const perPage = await Promise.all(
    pageIds.map(async (pageId) => {
      const page = catalog[pageId]!
      const [kpis, defaultKpiCharts] = await Promise.all([
        getKpisForPage(apiClient, pageId),
        getCharts(apiClient, pageId, page.defaultKpi),
      ])
      const registryFact = defaultKpiCharts[0]?.default_params?.['fact_table']
      const fact = typeof registryFact === 'string' ? registryFact : page.factTable
      return {
        pageId,
        route: page.route,
        kpis,
        fact,
        capabilities: PAGE_CAPABILITIES[pageId] ?? [],
      }
    }),
  )

  // One get_controls per DISTINCT fact.
  const distinctFacts = [...new Set(perPage.map((p) => p.fact))]
  const controlsByFact = new Map<string, Controls>(
    await Promise.all(
      distinctFacts.map(async (f) => [f, await getControls(apiClient, f)] as const),
    ),
  )

  // Dims per fact: count + first SAMPLE_LIMIT sorted values (dims_view rows, null dims skipped).
  const dims: Record<string, Record<string, OverviewDim>> = {}
  for (const [fact, c] of controlsByFact) {
    const sets: Record<string, Set<string>> = {}
    for (const row of c.dims_view) {
      if (row.dims == null) continue
      for (const [name, value] of Object.entries(row.dims)) {
        ;(sets[name] ??= new Set()).add(value)
      }
    }
    dims[fact] = Object.fromEntries(
      Object.entries(sets).map(([name, set]) => {
        const sorted = [...set].sort()
        return [name, { count: sorted.length, samples: sorted.slice(0, SAMPLE_LIMIT) }]
      }),
    )
  }

  // Value types per kpi_root from kpi_metadata (fact-independent; first controls suffices).
  const firstControls = controlsByFact.get(distinctFacts[0]!)!
  const valueTypes: Record<string, Array<{ key: string; label: string }>> = {}
  for (const item of firstControls.kpi_metadata.items) {
    if (item.value_type === null) continue
    const list = (valueTypes[item.kpi_root] ??= [])
    if (!list.some((v) => v.key === item.value_type)) {
      list.push({ key: item.value_type, label: item.display_value_type ?? item.value_type })
    }
  }

  // Timeframe terrain: min/max/floor aggregated across facts; frames + campaigns are
  // global timeframe rows (identical across facts) — read once, through the SAME
  // curation seam the Timeframe control uses (single source of truth).
  // min_max_dates arrives as ISO TIMESTAMPS on the live tenant ('2023-01-01T00:00:00');
  // the overview (and the welcome rendered from it) speaks calendar days — keep the
  // day part only. Lexical slice, no TZ math; idempotent on already-bare dates.
  const isoDay = (s: string): string => s.slice(0, 10)
  let minDate: string | null = null
  let maxDate: string | null = null
  let modelFloor: string | null = null
  for (const c of controlsByFact.values()) {
    const mm = c.min_max_dates
    if (mm.min_date && (!minDate || isoDay(mm.min_date) < minDate)) minDate = isoDay(mm.min_date)
    if (mm.max_date && (!maxDate || isoDay(mm.max_date) > maxDate)) maxDate = isoDay(mm.max_date)
    if (mm.model_period_min && (!modelFloor || isoDay(mm.model_period_min) > modelFloor))
      modelFloor = isoDay(mm.model_period_min)
  }
  const namedFrames = toSurfacedRollingTimeframes(firstControls.timeframe_rolling.items).map(
    (f) => ({ id: f.value, label: f.label, from: isoDay(f.from), to: isoDay(f.to) }),
  )
  // Campaigns C1: id + a compact predicate summary alongside the window, plus the raw item
  // (steering needs it — buildCampaignSelectionParams takes the full CampaignTimeslotItem).
  const campaignItemsByValue = new Map(
    firstControls.campaign_timeslots.items.map((i) => [i.value, i] as const),
  )
  const campaigns = toAbsoluteTimeframes(firstControls.campaign_timeslots.items).map((c) => {
    const item = campaignItemsByValue.get(c.value)!
    const pred = predicateOf(item)
    const filters = Object.entries(pred)
      .map(([dim, vals]) => `${dim}=${vals.join(',')}`)
      .join(', ')
    return {
      id: c.value,
      label: c.label,
      from: isoDay(c.from),
      to: isoDay(c.to),
      ...(filters.length > 0 ? { filters } : {}),
      item,
    }
  })

  return {
    pages: perPage,
    dims,
    valueTypes,
    timeframe: { modelFloor, minDate, maxDate, namedFrames, campaigns },
  }
}

/** Render the overview as the compact `## Dataset Overview` system-prefix section. */
export function renderOverviewBlock(o: DatasetOverview): string {
  const lines: string[] = [
    '## Dataset Overview',
    '(Terrain map — metadata only. Figures still come ONLY from query_kpi results.)',
  ]
  const tf = o.timeframe
  lines.push(
    `Data window: ${tf.minDate ?? 'unknown'} → ${tf.maxDate ?? 'unknown'}; model floor ${tf.modelFloor ?? 'unknown'}.`,
  )
  if (tf.namedFrames.length > 0) {
    lines.push(
      'Named rolling frames (steer via set_timeframe frame): ' +
        tf.namedFrames.map((f) => `${f.id}="${f.label}" (${f.from}→${f.to})`).join('; '),
    )
  }
  if (tf.campaigns.length > 0) {
    // C3 fix-wave #6: selection is identity-only (campaign=<id>) — the attribution tuple shown
    // per campaign here is a SUMMARY of what the app falls back to on facts that don't carry the
    // exact Campaign dim; it is NOT necessarily what a given walk applied (a carrying fact, e.g.
    // Net Spend on Marketing Invests, filters by the exact Campaign dim instead). Labelled
    // "attribution:", not "filters:", to keep that distinction visible in the prompt itself.
    lines.push(
      'Campaign windows (time window + identity; steer via set_campaign id or label). Each ' +
        "entry's attribution tuple is the fallback predicate on facts that don't carry the " +
        'exact Campaign dim — on carrying facts (the spend facts) selection filters exactly by ' +
        'campaign id instead: ' +
        tf.campaigns
          .map((c) => {
            const base = `"${c.label}" [id=${c.id}] ${c.from}→${c.to}`
            return c.filters ? `${base} (attribution: ${c.filters})` : base
          })
          .join('; '),
    )
  }
  for (const p of o.pages) {
    lines.push(
      `### ${p.pageId} (${p.route}) — fact ${p.fact}` +
        (p.capabilities.length > 0 ? ` — ${p.capabilities.join(', ')}` : ''),
    )
    lines.push(`KPIs: ${p.kpis.join(', ')}`)
  }
  lines.push('### Dimensions by fact table')
  for (const [fact, dimMap] of Object.entries(o.dims)) {
    const entries = Object.entries(dimMap)
    if (entries.length === 0) continue
    lines.push(
      `${fact}: ` +
        entries
          .map(([d, i]) => `"${d}" (${i.count} values, e.g. ${i.samples.join(', ')})`)
          .join('; '),
    )
  }
  const vts = Object.entries(o.valueTypes)
  if (vts.length > 0) {
    lines.push(
      '### Value types by KPI root\n' +
        vts
          .map(([root, list]) => `${root} → ${list.map((v) => `${v.key} "${v.label}"`).join(', ')}`)
          .join('\n'),
    )
  }
  return lines.join('\n')
}
